import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Download, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "nl-install-dismissed";
const DISMISS_DAYS = 14;

const recentlyDismissed = () => {
  const t = localStorage.getItem(DISMISS_KEY);
  return t ? Date.now() - Number(t) < DISMISS_DAYS * 86400000 : false;
};

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const InstallPrompt = () => {
  const location = useLocation();
  const [deferred, setDeferred] = useState<any>(null);
  const [mode, setMode] = useState<"hidden" | "native" | "ios">("hidden");

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
    if (isStandalone() || recentlyDismissed()) return;
    let timer: any;
    const onPrompt = (e: any) => {
      e.preventDefault();
      setDeferred(e);
      timer = setTimeout(() => setMode("native"), 4000);
    };
    const onInstalled = () => {
      setMode("hidden");
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (isIOS()) timer = setTimeout(() => setMode("ios"), 6000);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setMode("hidden");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome !== "accepted") localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setMode("hidden");
    setDeferred(null);
  };

  if (mode === "hidden" || location.pathname.startsWith("/admin")) return null;

  return (
    <div data-testid="install-prompt" onClick={mode === "native" ? install : undefined}
      className={`fixed z-[80] inset-x-3 bottom-3 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[380px] bg-[#121212] border border-[#7C3AED]/50 rounded-xl shadow-2xl shadow-black/60 p-4 animate-in slide-in-from-bottom-4 fade-in duration-300 ${mode === "native" ? "cursor-pointer" : ""}`}>
      <button onClick={(e) => { e.stopPropagation(); dismiss(); }} data-testid="install-dismiss"
        className="absolute top-2.5 right-2.5 text-gray-500 hover:text-white transition-colors">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="NewsLayer" className="w-11 h-11 rounded-xl border border-white/10 shrink-0" />
        <div className="min-w-0 pr-4">
          <p className="font-heading font-bold text-white text-sm">Install NewsLayer</p>
          {mode === "native" ? (
            <p className="text-gray-400 text-xs mt-1 leading-relaxed">
              Get the app experience — one tap from your home screen, instant loads and breaking-news alerts.
            </p>
          ) : (
            <p className="text-gray-400 text-xs mt-1 leading-relaxed">
              Get the app on your home screen in two quick steps:
            </p>
          )}
        </div>
      </div>
      {mode === "native" ? (
        <div className="flex gap-2 mt-3">
          <button onClick={(e) => { e.stopPropagation(); install(); }} data-testid="install-confirm"
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#7C3AED] text-white font-bold text-sm rounded-md py-2 hover:bg-[#6b2fd6] transition-colors">
            <Download className="h-4 w-4" /> Install app
          </button>
          <button onClick={(e) => { e.stopPropagation(); dismiss(); }} data-testid="install-later"
            className="px-4 text-sm text-gray-400 hover:text-white border border-white/15 rounded-md transition-colors">
            Not now
          </button>
        </div>
      ) : (
        <>
          <div className="mt-3 space-y-2" data-testid="install-ios-steps">
            <div className="flex items-center gap-2.5 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2">
              <span className="flex items-center justify-center h-5 w-5 rounded-full bg-[#7C3AED] text-white text-[11px] font-bold shrink-0">1</span>
              <p className="text-xs text-gray-300">
                Tap the <Share className="h-3.5 w-3.5 inline mx-1 text-[#00E5FF] -mt-0.5" />
                <span className="text-white font-semibold">Share</span> button in your browser's toolbar
              </p>
            </div>
            <div className="flex items-center gap-2.5 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2">
              <span className="flex items-center justify-center h-5 w-5 rounded-full bg-[#7C3AED] text-white text-[11px] font-bold shrink-0">2</span>
              <p className="text-xs text-gray-300">
                Scroll down and tap <PlusSquare className="h-3.5 w-3.5 inline mx-1 text-[#00E5FF] -mt-0.5" />
                <span className="text-white font-semibold">Add to Home Screen</span>
              </p>
            </div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); dismiss(); }} data-testid="install-later"
            className="w-full mt-3 text-sm text-gray-300 hover:text-white border border-white/15 hover:border-white/30 rounded-md py-2 transition-colors">
            Got it
          </button>
        </>
      )}
    </div>
  );
};
